import { useState, useEffect } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { Phone, Mail, MapPin } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_nh-spaces/artifacts/dgy84abe_NH%20INTERIOR%20DESIGN%20LOGO.png";

const navLinks = [
  { name: "Home", path: "/" },
  { name: "Services", path: "/services" },
  { name: "Projects", path: "/portfolio" },
  { name: "About", path: "/about" },
  { name: "Contact", path: "/contact" },
];

export default function Layout() {
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-cream side-decor">
      {/* Navigation */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 glass-nav ${
          isScrolled ? "shadow-sm" : ""
        }`}
        data-testid="main-navigation"
      >
        <div className="px-4 md:px-6 max-w-full">
          <nav className="flex items-center justify-between h-16 md:h-24">
            {/* Logo */}
            <Link to="/" className="relative z-10" data-testid="logo-link">
              <img
                src={LOGO_URL}
                alt="NH Interiors"
                className="h-10 md:h-16 w-auto"
              />
            </Link>

            {/* Navigation — always visible */}
            <div className="flex items-center gap-4 md:gap-10">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-[10px] md:text-sm uppercase tracking-[0.1em] md:tracking-[0.2em] font-body transition-colors duration-300 ${
                    location.pathname === link.path
                      ? "text-warmBrown"
                      : "text-black/70 hover:text-black"
                  }`}
                  data-testid={`nav-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main>
        <Outlet />
      </main>

      {/* Client Logo Banner */}
      <div className="w-full bg-soft-beige border-t border-black/5 py-8 overflow-hidden" data-testid="client-logo-banner">
        <p className="text-center text-black/30 text-xs uppercase tracking-[0.3em] font-body mb-6">
          Trusted By
        </p>
        <div className="relative">
          <div className="logo-scroll flex items-center gap-16 md:gap-24">
            {/* First set */}
            <img src="https://customer-assets.emergentagent.com/job_nh-spaces/artifacts/iox00rzi_Matrix%20logo%20%28all%20colours%29-03.png" alt="Matrix Fund Managers" className="h-10 md:h-14 w-auto object-contain opacity-70 flex-shrink-0" />
            <img src="https://customer-assets.emergentagent.com/job_nh-spaces/artifacts/7zzvvzvm_Alex%20Forbes.png" alt="Alex Forbes" className="h-10 md:h-14 w-auto object-contain opacity-70 flex-shrink-0" />
            <img src="https://customer-assets.emergentagent.com/job_nh-spaces/artifacts/kl4c75pk_B-Logic-Logo-Transparent-Background.png" alt="B-Logic" className="h-10 md:h-14 w-auto object-contain opacity-70 flex-shrink-0" />
            <img src="https://customer-assets.emergentagent.com/job_nh-spaces/artifacts/vsqf2qs8_Invest_Logo_square-removebg-preview.png" alt="Optimum Investment Group" className="h-14 md:h-20 w-auto object-contain opacity-70 flex-shrink-0" />
            <img src="https://customer-assets.emergentagent.com/job_nh-spaces/artifacts/hlpb83mk_LEAP_Logo-removebg-preview.png" alt="LEAP Science & Maths Schools" className="h-14 md:h-20 w-auto object-contain opacity-70 flex-shrink-0" />
            <img src="https://customer-assets.emergentagent.com/job_nh-spaces/artifacts/8fa56ya2_Prescient_Logo_RGB_Transparent%202.png" alt="Prescient" className="h-10 md:h-14 w-auto object-contain opacity-70 flex-shrink-0" />
            <img src="https://customer-assets.emergentagent.com/job_nh-spaces/artifacts/qazvexvv_Serengeti_Energy_Logo_White-removebg-preview.png" alt="Serengeti Energy" className="h-10 md:h-14 w-auto object-contain opacity-70 flex-shrink-0" />
            <img src="https://customer-assets.emergentagent.com/job_nh-spaces/artifacts/4e07fasn_standard-bank-vector-logo-small-removebg-preview.png" alt="Standard Bank" className="h-16 md:h-24 w-auto object-contain opacity-70 flex-shrink-0" />
            <img src="https://customer-assets.emergentagent.com/job_nh-spaces/artifacts/r9rto2od_Pnp.png" alt="Pick n Pay" className="h-10 md:h-14 w-auto object-contain opacity-70 flex-shrink-0" />
            <img src="https://customer-assets.emergentagent.com/job_nh-spaces/artifacts/16drhpqu_Morningstar-Logo-Vector-removebg-preview.png" alt="Morningstar" className="h-14 md:h-20 w-auto object-contain opacity-70 flex-shrink-0" />
            <img src="https://customer-assets.emergentagent.com/job_nh-spaces/artifacts/1tjr4tqc_Screenshot%202026-05-06%20155754.png" alt="Synsport" className="h-10 md:h-14 w-auto object-contain opacity-70 flex-shrink-0" />
            {/* Duplicate set for seamless loop */}
            <img src="https://customer-assets.emergentagent.com/job_nh-spaces/artifacts/iox00rzi_Matrix%20logo%20%28all%20colours%29-03.png" alt="Matrix Fund Managers" className="h-10 md:h-14 w-auto object-contain opacity-70 flex-shrink-0" />
            <img src="https://customer-assets.emergentagent.com/job_nh-spaces/artifacts/7zzvvzvm_Alex%20Forbes.png" alt="Alex Forbes" className="h-10 md:h-14 w-auto object-contain opacity-70 flex-shrink-0" />
            <img src="https://customer-assets.emergentagent.com/job_nh-spaces/artifacts/kl4c75pk_B-Logic-Logo-Transparent-Background.png" alt="B-Logic" className="h-10 md:h-14 w-auto object-contain opacity-70 flex-shrink-0" />
            <img src="https://customer-assets.emergentagent.com/job_nh-spaces/artifacts/vsqf2qs8_Invest_Logo_square-removebg-preview.png" alt="Optimum Investment Group" className="h-14 md:h-20 w-auto object-contain opacity-70 flex-shrink-0" />
            <img src="https://customer-assets.emergentagent.com/job_nh-spaces/artifacts/hlpb83mk_LEAP_Logo-removebg-preview.png" alt="LEAP Science & Maths Schools" className="h-14 md:h-20 w-auto object-contain opacity-70 flex-shrink-0" />
            <img src="https://customer-assets.emergentagent.com/job_nh-spaces/artifacts/8fa56ya2_Prescient_Logo_RGB_Transparent%202.png" alt="Prescient" className="h-10 md:h-14 w-auto object-contain opacity-70 flex-shrink-0" />
            <img src="https://customer-assets.emergentagent.com/job_nh-spaces/artifacts/qazvexvv_Serengeti_Energy_Logo_White-removebg-preview.png" alt="Serengeti Energy" className="h-10 md:h-14 w-auto object-contain opacity-70 flex-shrink-0" />
            <img src="https://customer-assets.emergentagent.com/job_nh-spaces/artifacts/4e07fasn_standard-bank-vector-logo-small-removebg-preview.png" alt="Standard Bank" className="h-16 md:h-24 w-auto object-contain opacity-70 flex-shrink-0" />
            <img src="https://customer-assets.emergentagent.com/job_nh-spaces/artifacts/r9rto2od_Pnp.png" alt="Pick n Pay" className="h-10 md:h-14 w-auto object-contain opacity-70 flex-shrink-0" />
            <img src="https://customer-assets.emergentagent.com/job_nh-spaces/artifacts/16drhpqu_Morningstar-Logo-Vector-removebg-preview.png" alt="Morningstar" className="h-14 md:h-20 w-auto object-contain opacity-70 flex-shrink-0" />
            <img src="https://customer-assets.emergentagent.com/job_nh-spaces/artifacts/1tjr4tqc_Screenshot%202026-05-06%20155754.png" alt="Synsport" className="h-10 md:h-14 w-auto object-contain opacity-70 flex-shrink-0" />
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-primary text-white" data-testid="footer">
        <div className="container-custom section-padding">
          {/* Large Typography CTA */}
          <div className="text-center mb-20">
            <p className="text-champagne uppercase tracking-[0.3em] text-xs mb-6 font-body">
              Ready to Transform Your Space?
            </p>
            <h2 className="font-heading text-4xl md:text-6xl lg:text-7xl text-white mb-8">
              Let's Create Something
              <br />
              <span className="italic text-champagne">Extraordinary</span>
            </h2>
            <Link
              to="/contact"
              className="btn-light inline-block"
              data-testid="footer-cta-button"
            >
              Get in Touch
            </Link>
          </div>

          {/* Footer Grid */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 py-12 border-t border-white/10">
            {/* Logo & Description */}
            <div className="md:col-span-2">
              <img
                src={LOGO_URL}
                alt="NH Interiors"
                className="h-12 w-auto brightness-0 invert mb-6"
              />
              <p className="text-white/70 font-body leading-relaxed max-w-md">
                Boutique interior design studio creating timeless spaces for
                corporate and residential clients. Over 11 years of excellence
                built on trust and referrals.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-white font-heading text-lg mb-6">Navigate</h4>
              <ul className="space-y-3">
                {navLinks.map((link) => (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      className="text-white/70 hover:text-champagne transition-colors duration-300 font-body text-sm"
                      data-testid={`footer-nav-${link.name.toLowerCase()}`}
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="text-white font-heading text-lg mb-6">Contact</h4>
              <ul className="space-y-4">
                <li>
                  <a
                    href="mailto:nadine@nhinteriors.co.za"
                    className="flex items-center gap-3 text-white/70 hover:text-champagne transition-colors duration-300 font-body text-sm"
                    data-testid="footer-email"
                  >
                    <Mail size={16} />
                    nadine@nhinteriors.co.za
                  </a>
                </li>
                <li>
                  <a
                    href="tel:+27827823723"
                    className="flex items-center gap-3 text-white/70 hover:text-champagne transition-colors duration-300 font-body text-sm"
                    data-testid="footer-phone"
                  >
                    <Phone size={16} />
                    +27 82 782 3723
                  </a>
                </li>
                <li>
                  <span className="flex items-center gap-3 text-white/70 font-body text-sm">
                    <MapPin size={16} />
                    Cape Town, South Africa
                  </span>
                </li>
              </ul>
            </div>
          </div>

          {/* Copyright */}
          <div className="pt-8 border-t border-white/10 text-center">
            <p className="text-white/50 text-xs font-body tracking-wider">
              © {new Date().getFullYear()} NH Interiors. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
